import { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import { Button } from "@mui/material";
import BackIcon from "@mui/icons-material/ArrowBackIosNew";

import { useAuth } from "react-oidc-context";
import { useNitroStore } from "../../store/Store";
import "./index.css";
import CloseIcon from "../../components/CloseIcon";
import CAModePicker from "./components/ModePicker";
import CAOffer from "./components/CAOffer";
import KochOrigins from "./components/KochOrigins";
import Competitors from "./components/Competitors";
import LoadingIcon from "../../components/Loading";
import { removeCity } from "../../utils/crap";
import { useIsMobile } from "../../hooks/useIsMobile";
import ProductFilter from "../../components/filters/ProductFilter";
import MonthFilterButton from "../../components/filters/MonthFilterButton";
import RetryPanel from "../../components/RetryPanel";
import { useIsComponentMounted } from "../../hooks/useIsComponentMounted";
import useEscapeKeyExit from "../../hooks/useEscapeKeyExit";
import { useNitroCAStore } from "../../store/CompetitiveAnalysisStore";
import RouteValidation from "./components/RouteValidation";
import { icons } from "../../svg";
import SetupOriginsWarning from "../../components/filters/SetupOriginsWarning";
import MobileFavoriteOriginsPanel from "../mobile/mobileFavoriteOriginPanel/mobileFavoriteOriginPanel";
import { useFavoriteOrigins } from "../../hooks/useFavoriteOrigins";
import { IPinExtension } from "../../types/ILocation";

const CompetitiveAnalysisPanel = ({
  reload,
  selectedLocation,
  railType,
  setRailType,
}: {
  reload: () => void;
  selectedLocation: IPinExtension;
  railType: "single" | "85+" | "100+" | undefined;
  setRailType: (value: "single" | "85+" | "100+" | undefined) => void;
}) => {
  const isMobile = useIsMobile(1334);
  const isComponentMounted = useIsComponentMounted();

  const [
    currentFilters,
    product,
    setProduct,
    mode,
    setMode,
    setFilters,
    showCompetitiveAnalysisModal,
    setShowCompetitiveAnalysisModal,
    uom,
    currency,
    setLabelType,
    fetchFavoriteOrigins,
    showMobileFavoriteOriginPanel,
    setShowMobileFavoriteOriginPanel,
  ] = useNitroStore((state) => [
    state.currentFilters,
    state.product,
    state.setProduct,
    state.mode,
    state.setMode,
    state.setFilters,
    state.showCompetitiveAnalysisModal,
    state.setShowCompetitiveAnalysisModal,
    state.uom,
    state.currency,
    state.setLabelType,
    state.fetchFavoriteOrigins,
    state.showMobileFavoriteOriginPanel,
    state.setShowMobileFavoriteOriginPanel,
  ]);

  const [
    load,
    reset,
    kochRecords,
    competitorRecords,
    hadError,
    isLoading,
    competitorOffer,
    setCompetitorOffer,
    changeRailType,
    setDisplayFreightDetailsPanel,
  ] = useNitroCAStore((state) => [
    state.load,
    state.reset,
    state.kochRecords,
    state.competitorRecords,
    state.hadError,
    state.isLoading,
    state.competitorOffer,
    state.setCompetitorOffer,
    state.changeRailType,
    state.setDisplayRailFreightDetailsPanel,
  ]);
  const auth = useAuth();
  const user = `${auth.user?.profile.given_name ?? ""} ${
    auth.user?.profile.family_name ?? ""
  }`.trim();
  const { showWarning } = useFavoriteOrigins(user, product);
  const [showWarningPanel, setShowWarningPanel] = useState(showWarning);

  const [activeFreightButton, setActiveFreightButton] = useState(null);

  useEffect(() => {
    if (!showMobileFavoriteOriginPanel) {
      setShowWarningPanel(showWarning);
    }
  }, [showWarning, showMobileFavoriteOriginPanel]);

  useEffect(() => {
    if (!showCompetitiveAnalysisModal) {
      reset();
    } else {
      setLabelType("prices");
    }
  }, [reset, setLabelType, showCompetitiveAnalysisModal]);

  useEscapeKeyExit({
    close: () => {
      setShowCompetitiveAnalysisModal(false);
    },
    active: showCompetitiveAnalysisModal,
  });

  useEffect(() => {
    if (!showCompetitiveAnalysisModal) return;
    if (!product || !mode) return;

    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocation,
    currentFilters,
    load,
    product,
    mode,
    showCompetitiveAnalysisModal,
    uom,
    currency,
    fetchFavoriteOrigins,
  ]);

  useEffect(() => {
    if (mode !== "RAIL") return;
    changeRailType(mode, railType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railType]);

  const productRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!isComponentMounted) return;
    if (!product) {
      console.log("Focus product");
      productRef.current?.focus();
    }
  }, [isComponentMounted, product, productRef]);

  const handleGoBack = () => {
    setShowWarningPanel(false);
  };

  const handleSetupOrigins = () => {
    setShowWarningPanel(false);
    setShowMobileFavoriteOriginPanel(true);
  };

  return (
    <>
      {showMobileFavoriteOriginPanel && (
        <div
          id="mobileFavoriteOriginsPanel"
          className="mobile-favorite-origins-overlay"
        >
          <MobileFavoriteOriginsPanel
            onClose={() => setShowMobileFavoriteOriginPanel(false)}
          />
        </div>
      )}
      <div className="competitiveAnalysisSearchBar">
        {isMobile && (
          <IconButton
            className="competitive-analysis-mobile-button"
            onClick={() => {
              setShowCompetitiveAnalysisModal(false);
            }}
          >
            <BackIcon />
          </IconButton>
        )}
        {isMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 10,
            }}
          >
            <div className="caProductFilter">
              {isMobile && (
                <ProductFilter
                  config={{
                    inputRef: productRef,
                    shadow: true,
                    opaque: true,
                    valueRequired: true,
                    className: "nitroAutocomplete",
                  }}
                  value={product}
                  setValue={(value) => {
                    setProduct(value);
                    setFilters({ ...currentFilters, product: value });
                  }}
                  isMobile={isMobile}
                />
              )}
            </div>
            {isMobile && (
              <div className="margin-right-20">
                <MonthFilterButton
                  value={currentFilters.month}
                  setValue={(value) => {
                    setFilters({ ...currentFilters, month: value });
                  }}
                  isMobile={isMobile}
                />
              </div>
            )}
            {isMobile && (
              <Button
                className="location-button"
                id="pinSelectConfirmButton"
                fullWidth
                variant="contained"
                onClick={() => {
                  setShowMobileFavoriteOriginPanel(true);
                }}
              >
                <img
                  data-tooltip-id="location-icon"
                  src={icons.LocationIcon}
                  alt="Location Icon"
                  className="quote-location-icon"
                />
              </Button>
            )}
            {showWarningPanel && (
              <SetupOriginsWarning
                onGoBack={handleGoBack}
                onSetupOrigins={handleSetupOrigins}
              />
            )}
          </div>
        )}
      </div>

      {hadError && (
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "white",
          }}
        >
          <RetryPanel retry={reload} />
        </div>
      )}

      {!hadError && (
        <div
          className="competitiveAnalysisBody favorite-origin-border"
          style={{
            zIndex: 20,
            backgroundColor: "#F7F7F7",
            boxShadow:
              "0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
            overflowY: "hidden",
            overflowX: "hidden",
            flexGrow: 1,
          }}
        >
          <RouteValidation reload={reload} />
          {/* HEADER */}
          <div className="competitiveAnalysisHeader">
            <div
              style={{
                margin: "0px 32px",
                fontWeight: 500,
                fontSize: 26,
                width: "100%",
                textAlign: "center",
              }}
            >
              <div>
                {removeCity(
                  selectedLocation.name || "",
                  selectedLocation.city || "",
                )}
              </div>

              <div style={{ fontSize: 16, fontWeight: 300, color: "#989898" }}>
                {selectedLocation.city}
              </div>
            </div>

            {!isMobile && (
              <div style={{ position: "absolute", right: 8, top: 8 }}>
                <CloseIcon
                  onClick={() => {
                    setShowCompetitiveAnalysisModal(false);
                    setDisplayFreightDetailsPanel(false);
                  }}
                />
              </div>
            )}
          </div>
          {/* END HEADER */}

          {/* BODY */}
          <div
            style={{
              flexGrow: 1,
              overflowY: "auto",
              padding: "0px 10px",
              paddingBottom: 20,
            }}
          >
            {isLoading && (
              <div style={{ paddingTop: "30%" }}>
                <LoadingIcon height={200} width={100} color="#005CA6" />
              </div>
            )}

            {!isLoading && !product && (
              <div
                style={{
                  paddingTop: "30%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: 16,
                    color: "#4b4b4b",
                    fontStyle: "italic",
                  }}
                >
                  Select a Product
                </div>
              </div>
            )}

            <div
              style={{
                paddingTop: 10,
                maxWidth: !product || isLoading ? 0 : undefined,
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              <CAModePicker
                value={mode}
                setValue={(value) => {
                  setMode(value);
                }}
                railType={railType}
                setRailType={setRailType}
              />

              <CAOffer
                record={kochRecords[0]}
                selectedLocation={selectedLocation}
              />

              <KochOrigins
                kochRecords={kochRecords}
                activeFreightButton={activeFreightButton}
                setActiveFreightButton={setActiveFreightButton}
              />

              <Competitors
                records={competitorRecords}
                reload={reload}
                competitorOffer={competitorOffer}
                setCompetitorOffer={setCompetitorOffer}
                activeFreightButton={activeFreightButton}
                setActiveFreightButton={setActiveFreightButton}
                product={product}
              />
            </div>
          </div>
          {/* END BODY */}
        </div>
      )}
    </>
  );
};

export default CompetitiveAnalysisPanel;
